/**
 * MAP NAVIGATION LOGIC DOCUMENTATION
 * 
 * This Map component handles navigation for two order types:
 * 
 * 1. CLICK & COLLECT (Customer picks up from pharmacy)
 *    - Single destination: Navigate customer to pharmacy
 *    - activeStep 2: Customer navigates to pharmacy
 * 
 * 2. BRINGZZ PICKUP (Shopper delivers to customer)
 *    
 *    A) WITH PRESCRIPTION (prescription array not empty):
 *       Flow: Shopper → Customer (pickup) → Pharmacy → Customer (delivery)
 *       - activeStep 0: Navigate to customer to pickup prescription
 *       - activeStep 2: Navigate to pharmacy with prescription
 *       - activeStep 6: Navigate back to customer to deliver medications
 *    
 *    B) WITHOUT PRESCRIPTION (prescription array empty):
 *       Flow: Shopper → Pharmacy → Customer
 *       - activeStep 0/2: Navigate directly to pharmacy (skip customer first)
 *       - activeStep 6: Navigate to customer to deliver medications
 * 
 * LOCATION DATA SOURCES:
 * - Shopper location: Real-time GPS from userLocation (current user's actual position)
 * - Customer location: From orderDetails.customer.address (geoLatitude, geoLongitude)
 * - Pharmacy location: From orderDetails.pharmacy (latitude, longitude)
 * 
 * KEY FUNCTIONS:
 * - hasPrescription(): Checks if prescription exists in order
 * - getDestinationForCurrentStep(): Returns appropriate destination based on order type, prescription status, and active step
 * - handleArrivalClick(): Handles arrival at each destination and triggers appropriate API calls
 */

import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import SearchBar from "./components/SearchBar";
import MapControls from "./components/MapControls";
import LoadingSpinner from "./components/LoadingSpinner";
import PermissionRequest from "./components/PermissionRequest";
import MapFooter from "./components/MapFooter";
import LoadingBar from "./components/LoadingBar";
import NavigationOverlay from "./components/NavigationOverlay";
import useMapEventLogger from "./hooks/useMapEventLogger";
import useLoadingManager from "./hooks/useLoadingManager";
import LocationManager from "./components/LocationManager";
import MapInitializer from "./components/MapInitializer";
import NavigationManager from "./components/NavigationManager";
import { useOrder } from 'context/OrderContext';
import { useNavigation } from 'context/NavigationContext';
import useRequest from 'hooks/useRequest';
import { DELIVERY_TYPES } from 'utils/constants';
import { BringzzButton, BringzzText, BringzzIcon } from '@bringzz/components';

// Dynamic location getters based on order details
const getPharmacyLocation = (orderDetails) => {
    if (!orderDetails?.pharmacy) return null;
    
    const { latitude, longitude, name } = orderDetails.pharmacy;
    
    if (latitude && longitude) {
        return {
            latitude: parseFloat(latitude),
            longitude: parseFloat(longitude),
            title: name || "Pharmacy",
            subtitle: "Click & Collect Location"
        };
    }
    
    console.warn('Pharmacy coordinates missing in order details');
    return null;
};

// Helper to check if prescription exists
const hasPrescription = (orderDetails) => {
    return orderDetails?.prescription
};

// Get customer location from order details
const getCustomerLocation = (orderDetails) => {
    if (!orderDetails?.customer?.address) return null;
    
    const { geoLatitude, geoLongitude, streetName, cityName } = orderDetails.customer.address;
    
    if (geoLatitude && geoLongitude) {
        return {
            latitude: parseFloat(geoLatitude),
            longitude: parseFloat(geoLongitude),
            title: "Customer Location",
            subtitle: cityName || streetName || "Delivery Address"
        };
    }
    
    console.warn('Customer coordinates missing in order details');
    return null;
};

// Note: Shopper location is obtained from userLocation state (GPS)
// No need for a separate function as it's already in the correct format

// Remove static locations - will use dynamic data from order context

const MapView = () => {
    const [map, setMap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userLocation, setUserLocation] = useState(null);
    const [permissionStatus, setPermissionStatus] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const [routeInfo, setRouteInfo] = useState(null);
    const [preservedEstimates, setPreservedEstimates] = useState({
        estimatedTime: null,
        distance: null,
        instruction: null,
        nextManeuver: null,
        currentStepDistance: null
    });
    const mapRef = useRef(null);
    const locationManagerRef = useRef(null);
    const navigationManagerRef = useRef(null);
    const hasStartedNavigation = useRef(false);

    const { orderDetails, activeStep, proceedToNextStep } = useOrder();
    const { navigate, backPress } = useNavigation();
    const { sendRequest } = useRequest();

    const logMapEvent = useMapEventLogger();
    const { updateLoadingState, isAnyLoading } = useLoadingManager();

    console.log('[MapView] Initial render:', {
        orderDetails: orderDetails?.id,
        activeStep,
        hasMap: !!map,
        hasUserLocation: !!userLocation,
        isNavigating
    });

    const getDestinationForCurrentStep = useCallback(() => {
        console.log('🎯 [Get Destination]', {
            orderType: orderDetails?.type,
            activeStep,
            numericType: Number(orderDetails?.type),
            hasPrescription: hasPrescription(orderDetails),
            pharmacyCoords: orderDetails?.pharmacy ? {
                lat: orderDetails.pharmacy.latitude,
                lng: orderDetails.pharmacy.longitude,
                name: orderDetails.pharmacy.name
            } : null
        });

        if (!orderDetails) return null;

        let destination = null;
        if (Number(orderDetails.type) === DELIVERY_TYPES.CLICK_AND_COLLECT) {
            // Use dynamic pharmacy location from order details
            destination = getPharmacyLocation(orderDetails);
            
            if (!destination) {
                console.error('Failed to get pharmacy location for Click & Collect');
            }
        } else if (Number(orderDetails.type) === DELIVERY_TYPES.BRINGZZ_PICKUP) {
            const prescriptionExists = hasPrescription(orderDetails);
            
            if (prescriptionExists) {
                // WITH PRESCRIPTION: Customer -> Pharmacy -> Customer
                if (activeStep === 0) {
                    // First go to customer to pickup prescription
                    destination = getCustomerLocation(orderDetails);
                    if (!destination) {
                        console.error('Failed to get customer location for prescription pickup');
                    }
                } else if (activeStep === 2) {
                    // Then go to pharmacy with prescription
                    destination = getPharmacyLocation(orderDetails);
                    
                    if (!destination) {
                        console.error('Failed to get pharmacy location for Bringzz pickup');
                    }
                } else if (activeStep === 6) {
                    // Finally deliver back to customer
                    destination = getCustomerLocation(orderDetails);
                    if (!destination) {
                        console.error('Failed to get customer location for delivery');
                    }
                }
            } else {
                // WITHOUT PRESCRIPTION: Pharmacy -> Customer
                if (activeStep === 0 || activeStep === 2) {
                    // Go directly to pharmacy (handle both initial steps)
                    destination = getPharmacyLocation(orderDetails);
                    
                    if (!destination) {
                        console.error('Failed to get pharmacy location for Bringzz pickup');
                    }
                } else if (activeStep === 6) {
                    // Then deliver to customer
                    destination = getCustomerLocation(orderDetails);
                    if (!destination) {
                        console.error('Failed to get customer location for delivery');
                    }
                }
            }
        }

        console.log('📍 [Selected Destination]', destination);
        return destination;
    }, [orderDetails, activeStep]);

    const handleStartNavigation = useCallback(async (currentLocation = null) => {
        const locationToUse = currentLocation || userLocation;
        
        console.log('🎯 [Start Navigation]', {
            hasOrder: !!orderDetails,
            orderType: orderDetails?.type,
            activeStep,
            hasLocation: !!locationToUse,
            location: locationToUse,
            hasNavigationRef: !!navigationManagerRef.current
        });

        if (!orderDetails) {
            console.log('❌ [Navigation Error] No active order');
            return;
        }

        if (navigationManagerRef.current && locationToUse) {
            setIsNavigating(true);
            const destination = getDestinationForCurrentStep();
            
            console.log('📍 [Navigation Destination]', {
                from: locationToUse,
                to: destination,
                isNavigating
            });

            if (destination) {
                // Validate coordinates
                if (!destination.latitude || !destination.longitude || 
                    isNaN(destination.latitude) || isNaN(destination.longitude)) {
                    console.error('❌ [Navigation Error] Invalid destination coordinates:', destination);
                    setIsNavigating(false);
                    return;
                }
                
                try {
                    await navigationManagerRef.current.startNavigationTo(destination);
                    console.log('✅ [Navigation Started] Successfully started navigation');
                } catch (error) {
                    console.error('❌ [Navigation Error]', error);
                    setIsNavigating(false);
                }
            } else {
                console.log('❌ [Navigation Error] No destination found for current step');
            }
        } else {
            console.log('❌ [Navigation Error] Missing navigation manager or location');
        }
    }, [orderDetails, userLocation, getDestinationForCurrentStep, activeStep]);

    const handleLocationUpdate = useCallback((location) => {
        console.log('📱 [Location Update]', {
            hasLocation: !!location,
            location,
            hasOrder: !!orderDetails,
            hasStarted: hasStartedNavigation.current,
            isNavigating
        });

        setUserLocation(location);
        
        if (location && orderDetails && !hasStartedNavigation.current && navigationManagerRef.current) {
            console.log('🔄 [Auto-starting Navigation]');
            hasStartedNavigation.current = true;
            handleStartNavigation(location);
        }
    }, [orderDetails, handleStartNavigation, isNavigating]);

    const handleExitNavigation = useCallback(() => {
        if (navigationManagerRef.current) {
            navigationManagerRef.current.exitNavigation();
            setIsNavigating(false);
            setRouteInfo(null);
            setPreservedEstimates({
                estimatedTime: null,
                distance: null,
                instruction: null,
                nextManeuver: null,
                currentStepDistance: null
            });
            navigate('/home');
        }
    }, [navigate]);

    // Initialize navigation when all dependencies are ready
    useEffect(() => {
        console.log('🔄 [Navigation Init Effect]', {
            hasMap: !!map,
            hasUserLocation: !!userLocation,
            hasOrder: !!orderDetails,
            hasStarted: hasStartedNavigation.current,
            routeInfo: !!routeInfo
        });

        if (map && userLocation && orderDetails && !hasStartedNavigation.current) {
            console.log('🚀 [Starting Initial Navigation]');
            hasStartedNavigation.current = true;
            handleStartNavigation();
        }
    }, [map, userLocation, orderDetails, handleStartNavigation]);

    // Reset navigation when order details or active step changes
    useEffect(() => {
        console.log('🔄 [Navigation Reset Effect]', {
            orderId: orderDetails?.id,
            activeStep,
            hasLocation: !!userLocation,
            hasStarted: hasStartedNavigation.current
        });

        if (orderDetails) {
            hasStartedNavigation.current = false;
            if (userLocation) {
                handleStartNavigation();
            }
        }
    }, [orderDetails?.id, activeStep, userLocation, handleStartNavigation]);

    const mapInitializer = MapInitializer({
        onMapCreated: useCallback((newMap) => {
            console.log('[onMapCreated] Map initialized');
            setMap(newMap);
            setLoading(false);
            if (newMap) {
                updateLoadingState('map', false);
            }
        }, [updateLoadingState]),
        logMapEvent
    });

    const handleLocationRequest = async () => {
        try {
            if (!locationManagerRef.current) return;
            
            const location = await locationManagerRef.current.getUserLocation();
            setPermissionStatus('granted');
            
            if (map) {
                const coordinate = new window.mapkit.Coordinate(location.latitude, location.longitude);
                const region = new window.mapkit.CoordinateRegion(
                    coordinate,
                    new window.mapkit.CoordinateSpan(0.005, 0.005)
                );
                map.region = region;
            } else {
                mapInitializer.initializeMap(mapRef, location);
            }
        } catch (error) {
            logMapEvent('Location Request Error', error);
            setPermissionStatus('denied');
            if (!map) {
                mapInitializer.initializeMap(mapRef);
            }
        }
    };

    const handleMyLocation = async () => {
        logMapEvent('My Location Button Clicked', null);
        if (map && locationManagerRef.current) {
            try {
                const location = await locationManagerRef.current.getUserLocation();
                const coordinate = new window.mapkit.Coordinate(location.latitude, location.longitude);
                const region = new window.mapkit.CoordinateRegion(
                    coordinate,
                    new window.mapkit.CoordinateSpan(0.005, 0.005)
                );
                map.region = region;
            } catch (error) {
                console.error('Error getting location:', error);
            }
        } else {
            await handleLocationRequest();
        }
    };

    const handleArrivalClick = async () => {
        if (!orderDetails) {
            console.log('No active order');
            return;
        }

        console.log("YEESSS")

        if (orderDetails.type == DELIVERY_TYPES.CLICK_AND_COLLECT && activeStep === 2) {
            await sendRequest({
                method: 'POST',
                url: '/module/order/customerHasArrivedAtThePharmacy',
                data: {
                    orderUUID: orderDetails.integrationId
                }
            });
            proceedToNextStep();
            navigate('/matchmaking/customer/3');
        } else if (orderDetails.type == DELIVERY_TYPES.BRINGZZ_PICKUP) {
            const prescriptionExists = hasPrescription(orderDetails);

            console.log(prescriptionExists)
            console.log("HEREE")
            
            if (prescriptionExists) {
                console.log("NOOO")

                // WITH PRESCRIPTION flow
                if (activeStep === 0) {
                    // Arrived at customer for prescription pickup
                    await sendRequest({
                        method: 'POST',
                        url: '/module/order/shopperHasArrivedAtTheCustomer',
                        data: {
                            orderUUID: orderDetails.integrationId
                        }
                    });
                    proceedToNextStep();
                    navigate('/matchmaking/shopper/1');
                } else if (activeStep === 2) {
                    // Arrived at pharmacy with prescription
                    await sendRequest({
                        method: 'POST',
                        url: '/module/order/shopperHasArrivedAtThePharmacy',
                        data: {
                            orderUUID: orderDetails.integrationId
                        }
                    });
                    proceedToNextStep();
                    navigate('/matchmaking/shopper/3');
                } else if (activeStep === 6) {
                    // Arrived back at customer for delivery
                    await sendRequest({
                        method: 'POST',
                        url: '/module/order/shopperHasArrivedAtTheCustomer',
                        data: {
                            orderUUID: orderDetails.integrationId
                        }
                    });
                    proceedToNextStep();
                    navigate('/matchmaking/shopper/7');
                }
            } else {
                // WITHOUT PRESCRIPTION flow
                if (activeStep === 0 || activeStep === 2) {
                    // Arrived at pharmacy (skip customer first visit)
                    await sendRequest({
                        method: 'POST',
                        url: '/module/order/shopperHasArrivedAtThePharmacy',
                        data: {
                            orderUUID: orderDetails.integrationId
                        }
                    });
                    proceedToNextStep();
                    // Navigate to appropriate shopper screen based on step
                    navigate(activeStep === 0 ? '/matchmaking/shopper/1' : '/matchmaking/shopper/3');
                } else if (activeStep === 6) {
                    // Arrived at customer for delivery
                    await sendRequest({
                        method: 'POST',
                        url: '/module/order/shopperHasArrivedAtTheCustomer',
                        data: {
                            orderUUID: orderDetails.integrationId
                        }
                    });
                    proceedToNextStep();
                    navigate('/matchmaking/shopper/7');
                }
            }
        }
    };

    useEffect(() => {
        const checkPermissionAndInit = async () => {
            updateLoadingState('permissions', true);
            try {
                const permission = await navigator.permissions.query({ name: 'geolocation' });
                logMapEvent('Permission Status', permission.state);
                setPermissionStatus(permission.state);

                if (permission.state === 'granted') {
                    handleLocationRequest();
                } else {
                    mapInitializer.initializeMap(mapRef);
                }

                permission.addEventListener('change', () => {
                    logMapEvent('Permission Changed', permission.state);
                    setPermissionStatus(permission.state);
                });
            } catch (error) {
                logMapEvent('Permission Check Error', error);
                console.error('Error checking permissions:', error);
                mapInitializer.initializeMap(mapRef);
            } finally {
                updateLoadingState('permissions', false);
            }
        };

        checkPermissionAndInit();

        return () => {
            if (map) {
                logMapEvent('Cleaning up map', null);
                map.destroy();
            }
        };
    }, []);

    // Get navigation status based on order type and active step
    const getNavigationStatus = () => {
        if (!orderDetails) {
            return 'No active navigation';
        }

        console.log("orderDetails", orderDetails)
        console.log("activeStep", activeStep)

        if (Number(orderDetails.type) === DELIVERY_TYPES.CLICK_AND_COLLECT) {
            const pharmacyName = orderDetails.pharmacy?.name || 'pharmacy';
            return `Navigating to ${pharmacyName}...`;
        } else if (Number(orderDetails.type) === DELIVERY_TYPES.BRINGZZ_PICKUP) {
            const prescriptionExists = hasPrescription(orderDetails);
            console.log("activeStep-bringzz", activeStep, "hasPrescription:", prescriptionExists)
            
            if (prescriptionExists) {
                // WITH PRESCRIPTION
                if (activeStep === 0) {
                    return 'Navigating to customer for prescription pickup...';
                } else if (activeStep === 2) {
                    const pharmacyName = orderDetails.pharmacy?.name || 'pharmacy';
                    return `Navigating to ${pharmacyName} with prescription...`;
                } else if (activeStep === 6) {
                    return 'Delivering order to customer...';
                }
            } else {
                // WITHOUT PRESCRIPTION
                if (activeStep === 0 || activeStep === 2) {
                    const pharmacyName = orderDetails.pharmacy?.name || 'pharmacy';
                    return `Navigating directly to ${pharmacyName}...`;
                } else if (activeStep === 6) {
                    return 'Delivering order to customer...';
                }
            }
        }
        return 'Navigation in progress...';
    };

    const handleRouteUpdate = useCallback((routeData) => {
        // Enhanced logging for Apple MapKit navigation data
        console.log('🚴 [NAVIGATION DATA - Full Route]', {
            hasRouteData: !!routeData,
            totalSteps: routeData?.steps?.length,
            expectedTime: routeData?.expectedTravelTime,
            totalDistance: routeData?.distance,
            currentStepIndex: routeData?.currentStepIndex,
            userLocation: routeData?.userLocation,
            routeName: routeData?.name
        });

        if (routeData) {
            // Ensure we have a valid currentStepIndex
            const currentStepIndex = typeof routeData.currentStepIndex === 'number' ? routeData.currentStepIndex : 0;
            const currentStep = routeData.steps?.[currentStepIndex] || routeData.steps?.[0];
            const nextStep = routeData.steps?.[currentStepIndex + 1];
            
            // Log current navigation step details
            console.log('📍 [CURRENT STEP DATA]', {
                stepNumber: currentStepIndex + 1,
                totalSteps: routeData.steps?.length,
                currentStep: {
                    instructions: currentStep?.instructions,
                    maneuver: currentStep?.maneuver,
                    distance: currentStep?.distance,
                    path: currentStep?.path?.length,
                    stepIndex: currentStep?.stepIndex
                },
                nextStep: nextStep ? {
                    instructions: nextStep?.instructions,
                    maneuver: nextStep?.maneuver,
                    distance: nextStep?.distance
                } : 'Destination reached'
            });
            
            // Log all available steps for debugging
            console.log('🗺️ [ALL NAVIGATION STEPS]', 
                routeData.steps?.map((step, idx) => ({
                    step: idx + 1,
                    instruction: step.instructions,
                    maneuver: step.maneuver,
                    distance: step.distance,
                    isCurrent: idx === currentStepIndex
                }))
            );

            // Smart navigation logic: if current step has no instructions, show next step
            let displayStep = currentStep;
            let displayStepDistance = currentStep?.distance || 0;
            
            // If current step has empty instructions and there's a next step, use next step
            if ((!currentStep?.instructions || currentStep.instructions === "") && nextStep) {
                displayStep = nextStep;
                displayStepDistance = (currentStep?.distance || 0) + (nextStep?.distance || 0);
                console.log('🔄 [Using Next Step]', {
                    reason: 'Current step has empty instructions',
                    displayingStep: displayStep,
                    combinedDistance: displayStepDistance
                });
            }

            const updatedRouteInfo = {
                ...routeData,
                estimatedTime: routeData.expectedTravelTime,
                distance: routeData.distance,
                currentStepIndex: currentStepIndex,
                nextManeuver: displayStep?.maneuver || preservedEstimates.nextManeuver || '3',
                instruction: displayStep?.instructions || preservedEstimates.instruction || 'Continue on route',
                currentStepDistance: displayStepDistance || preservedEstimates.currentStepDistance || 0
            };
            
            // Preserve all navigation data for display
            if (routeData.expectedTravelTime && routeData.distance) {
                setPreservedEstimates({
                    estimatedTime: routeData.expectedTravelTime,
                    distance: routeData.distance,
                    instruction: displayStep?.instructions || preservedEstimates.instruction,
                    nextManeuver: displayStep?.maneuver || preservedEstimates.nextManeuver,
                    currentStepDistance: displayStepDistance || preservedEstimates.currentStepDistance
                });
            }
            
            setRouteInfo(updatedRouteInfo);
        } else {
            setRouteInfo(null);
        }
    }, []);

    if (permissionStatus === null) {
        return (
            <>
                <LoadingBar isLoading={true} />
                <LoadingSpinner />
            </>
        );
    }

    if (loading && permissionStatus === 'granted') {
        return <LoadingSpinner />;
    }

    return (
        <Fragment>
            <LoadingBar isLoading={isAnyLoading} />
            <div className="flex flex-col h-screen w-full">
                <div className="relative w-full flex-grow overflow-hidden">
                    <MapControls onLocationClick={handleMyLocation} />
                    
                    {permissionStatus === 'prompt' && (
                        <PermissionRequest onRequestPermission={handleLocationRequest} />
                    )}

                    {/* Only show Arrived button if there's an active order */}
                    {orderDetails && (
                        <div className="absolute top-4 right-4 z-50">
                            <BringzzButton
                                onClick={handleArrivalClick}
                                className="bg-magic-lilac text-white"
                            >
                                Arrived
                            </BringzzButton>
                        </div>
                    )}

                    {/* Always show when navigating */}
                    {isNavigating && (
                        <NavigationOverlay 
                            nextManeuver={routeInfo?.nextManeuver || preservedEstimates.nextManeuver} 
                            distance={routeInfo?.currentStepDistance || preservedEstimates.currentStepDistance}
                            instruction={routeInfo?.instruction || preservedEstimates.instruction}
                        />
                    )}

                    <div 
                        ref={mapRef}
                        className="w-full h-full"
                    />

                    <LocationManager
                        ref={locationManagerRef}
                        map={map}
                        onLocationUpdate={handleLocationUpdate}
                        updateLoadingState={updateLoadingState}
                        logMapEvent={logMapEvent}
                        isNavigating={isNavigating}
                    />

                    <NavigationManager
                        ref={navigationManagerRef}
                        map={map}
                        userLocation={userLocation}
                        updateLoadingState={updateLoadingState}
                        onRouteUpdate={handleRouteUpdate}
                    />
                </div>

                {/* Only show navigation footer if there's an active order */}
                {orderDetails && (
                    <div className="bg-black">
                        <div className='flex justify-center items-center space-x-4 p-2 bg-magic-lilac/80'>
                            <BringzzText tag='h4' className={"text-white"}>
                                {(routeInfo?.estimatedTime || preservedEstimates.estimatedTime)
                                    ? `Estimated time: ${Math.ceil((routeInfo?.estimatedTime || preservedEstimates.estimatedTime) / 60)} min` 
                                    : 'Calculating time...'}
                            </BringzzText>
                            <BringzzText tag='h4' className={"text-white"}>|</BringzzText>
                            <BringzzText tag='h4' className={"text-white"}>
                                {(routeInfo?.distance || preservedEstimates.distance)
                                    ? `Estimated distance: ${((routeInfo?.distance || preservedEstimates.distance) / 1000).toFixed(1)} km` 
                                    : 'Calculating distance...'}
                            </BringzzText>
                        </div>
                        <div className="bg-magic-lilac w-full p-4">
                            <BringzzText tag="h4" className="font-normal text-white">
                                Order #{orderDetails.id}
                            </BringzzText>
                            <BringzzText tag="h3" className="text-white">
                                {getNavigationStatus()}
                            </BringzzText>
                            <div className="flex justify-between items-center mt-4">
                                <div className="flex items-center space-x-2">
                                    <BringzzText onClick={() => backPress()} tag="h4" className="text-white font-normal underline cursor-pointer">
                                        View details
                                    </BringzzText>
                                    <BringzzIcon icon="IconChevronDown" folder="LineIcons" size="12" className="text-white" />
                                </div>
                                <BringzzButton 
                                    className="border-white border text-white" 
                                    onClick={handleExitNavigation}
                                >
                                    Exit navigation
                                </BringzzButton>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

export default MapView;
